import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SyncIcon from '@mui/icons-material/Sync';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { ConstantActions } from '../../../Services/_actions/index.js';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider.jsx';
import WabaProfilePanel from '../components/WabaProfilePanel.jsx';
import useTemplateSx from '../hooks/useTemplateSx';
import { wabaSelectedAppAtom } from '../state/templateAtoms';
import { templateService } from '../services/templateService';
import { TEMPLATE_BASE_PATH } from '../constants/templateApi';
import { TEMPLATE_PERMISSIONS } from '../constants/templatePermissions';
import { normalizeWabaApp } from '../utils/normalizeTemplate';
import { radiusPx } from '../../../theme/tokens';

export default function WabaAppDetailsPage() {
  return (
    <FeatureThemeProvider>
      <WabaAppDetailsPageContent />
    </FeatureThemeProvider>
  );
}

function WabaAppDetailsPageContent() {
  const styles = useTemplateSx();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const constActions = ConstantActions();
  const [app, setApp] = useRecoilState(wabaSelectedAppAtom);
  const loggedInUser = useRecoilValue(userInformationAtom);

  const [tab, setTab] = useState(0);
  const [callbackUrl, setCallbackUrl] = useState('');
  const [isCallbackEnabled, setIsCallbackEnabled] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const [messageEvents, setMessageEvents] = useState({
    enqueued: true,
    failed: true,
    read: true,
    sent: true,
    delivered: true,
    delete: true,
    others: true,
  });
  const [systemEvents, setSystemEvents] = useState({
    templateEvents: true,
    accountRelatedEvents: true,
  });

  const canManage = constActions.userHasPermission([
    TEMPLATE_PERMISSIONS.wabaAppList,
    TEMPLATE_PERMISSIONS.metaEmbeddedSignup,
  ]);

  useEffect(() => {
    if (!app) {
      navigate(`${TEMPLATE_BASE_PATH}/manager/app-list`, { replace: true });
      return;
    }

    const url = app?.webhook?.callback_url || '';
    const registered = app?.webhook?.registered === true;
    setCallbackUrl(url);
    setIsCallbackEnabled(registered || Boolean(url));
  }, [app, navigate]);

  if (!app) {
    return (
      <Box sx={{ ...styles.pageRoot, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const breadcrumbLabel = tab === 0 ? 'Profile' : 'Webhook';

  const handleSaveProfile = async (payload) => {
    setSavingProfile(true);
    try {
      const res = await templateService.updateWabaApp(payload);
      if (res?.status !== 200 || !res?.data) {
        const message =
          (typeof res?.data === 'string' && res.data) ||
          res?.data?.general_error ||
          'Failed to update WABA details';
        showSnackBar(message, 'error');
        return false;
      }

      const updatedApp = normalizeWabaApp(res.data);
      setApp(updatedApp);
      showSnackBar('WABA details updated successfully', 'success');
      return updatedApp;
    } catch (error) {
      const message =
        error?.response?.data?.data?.general_error ||
        error?.message ||
        'Failed to update WABA details';
      showSnackBar(message, 'error');
      return false;
    } finally {
      setSavingProfile(false);
    }
  };

  const handleRegisterWebhook = async () => {
    if (!callbackUrl.trim()) {
      showSnackBar('Callback URL is required', 'error');
      return;
    }

    const phoneNumberId = app?.wa_phone_number_id;
    if (!phoneNumberId) {
      showSnackBar('No phone number ID found for this app', 'error');
      return;
    }

    const payload = {
      company_id:
        loggedInUser?.profile?.organisation?.organisation_id || app?.company_id,
      phone_number_id: phoneNumberId,
      callback_url: callbackUrl.trim(),
      verify_token: app?.webhook?.verify_token || '12345',
      message_events: messageEvents,
      system_events: systemEvents,
    };

    setSavingWebhook(true);
    try {
      await templateService.registerWebhook(payload);
      showSnackBar('Webhook saved successfully', 'success');
    } catch (error) {
      showSnackBar(error?.message || 'Failed to register webhook', 'error');
    } finally {
      setSavingWebhook(false);
    }
  };

  const appPhoneNumber = app?.wa_phone_number || app?.phone_number || '';

  const handleOpenPhoneHealth = () => {
    navigate(
      `${TEMPLATE_BASE_PATH}/template-list?view=health&health_tab=0&waba_number=${encodeURIComponent(appPhoneNumber)}`
    );
  };

  const handleOpenCallbackSimulator = () => {
    navigate(`${TEMPLATE_BASE_PATH}/template-list?view=health&health_tab=3`);
  };

  return (
    <Box sx={styles.pageRoot}>
      <DynamicBreadcrumbs
        items={[
          { label: <IconButton color="primary"><HomeIcon /></IconButton>, href: '/' },
          { label: 'App List', href: `${TEMPLATE_BASE_PATH}/manager/app-list` },
          { label: breadcrumbLabel },
        ]}
        onBreadcrumbClick={(_event, item) => {
          if (item?.href) navigate(item.href);
        }}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Tabs value={tab} onChange={(_event, value) => setTab(value)}>
          <Tab label="Profile" />
          <Tab label="Webhook" />
        </Tabs>

        <Button
          variant="outlined"
          startIcon={<MonitorHeartIcon sx={{ color: '#059669' }} />}
          onClick={handleOpenPhoneHealth}
          sx={{
            borderColor: '#10b981',
            color: '#059669',
            borderRadius: radiusPx.md,
            '&:hover': {
              borderColor: '#059669',
              bgcolor: '#ecfdf5',
            },
          }}
        >
          Check WABA Health
        </Button>
      </Box>

      {tab === 0 ? (
        <WabaProfilePanel
          app={app}
          canEdit={canManage}
          isSaving={savingProfile}
          onSave={handleSaveProfile}
          styles={styles}
        />
      ) : null}

      {tab === 1 ? (
        <Box sx={styles.wabaProfileCard}>
          <Typography variant="h5" sx={styles.wabaProfileTitle}>
            Webhook Configuration
          </Typography>

          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                checked={isCallbackEnabled}
                onChange={() => setIsCallbackEnabled((prev) => !prev)}
              />
            }
            label="Enable callback URL"
          />

          <TextField
            fullWidth
            size="small"
            label="Callback URL"
            value={callbackUrl}
            onChange={(e) => setCallbackUrl(e.target.value)}
            disabled={!isCallbackEnabled}
            sx={{ mt: 2, mb: 3 }}
          />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Message Events
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {Object.keys(messageEvents).map((key) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={messageEvents[key]}
                    onChange={(e) =>
                      setMessageEvents((prev) => ({ ...prev, [key]: e.target.checked }))
                    }
                  />
                }
                label={key}
              />
            ))}
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            System Events
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {Object.keys(systemEvents).map((key) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={systemEvents[key]}
                    onChange={(e) =>
                      setSystemEvents((prev) => ({ ...prev, [key]: e.target.checked }))
                    }
                  />
                }
                label={key}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="contained"
              onClick={handleRegisterWebhook}
              disabled={savingWebhook || !isCallbackEnabled}
              sx={styles.createButton}
            >
              {savingWebhook ? 'Saving…' : 'Save Webhook'}
            </Button>

            {/* <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={handleOpenCallbackSimulator}
              sx={{ borderRadius: radiusPx.md }}
            >
              Test Webhook Callback API
            </Button> */}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
